import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

// custom hook to fetch company info (subscription agreement)
const useCompanyInfo = (enabled = true) => {
  return useQuery({
    queryKey: ["company-info"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        ApiEndPoints.COMMON.companyInfo
      );
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: enabled,
    retry: 1,
  });
};

const SubscriptionAgreementDialog = ({ open, onClose }) => {
  // Fetch company info only when dialog is open
  const {
    data: companyInfoData,
    isLoading: isCompanyInfoLoading,
    error: companyInfoError,
  } = useCompanyInfo(open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // zIndex must be higher than the subscription dialog (1400) and its overlay
      sx={{ zIndex: 1600 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#7c28d6",
          fontWeight: 700,
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
          subscription agreement
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: "#666" }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            maxHeight: "60vh",
            overflowY: "auto",
            pr: 1,
            // Force list styles to show bullets
            "& ul": {
              listStyle: "disc",
              listStyleType: "disc",
              paddingLeft: "24px",
              marginLeft: "0",
            },
            "& li": {
              display: "list-item",
            },
          }}
        >
          {isCompanyInfoLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : companyInfoError ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#FFF3E0",
                color: "#E65100",
              }}
            >
              <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                failed to load subscription agreement. please try again later.
              </Typography>
            </Box>
          ) : (() => {
            // Try multiple possible response structures
            const subscriptionAgreement =
              companyInfoData?.subscription_agreement ||
              companyInfoData?.data?.subscription_agreement;

            return subscriptionAgreement ? (
              <Box
                sx={{
                  textTransform: "lowercase",
                  "& h1": {
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 2,
                    color: "#000",
                  },
                  "& h2": {
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    mb: 1.5,
                    mt: 2,
                    color: "#000",
                  },
                  "& h3": {
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    mb: 1,
                    mt: 1.5,
                    color: "#000",
                  },
                  "& p": {
                    mb: 1.5,
                    lineHeight: 1.8,
                    color: "#333",
                  },
                  "& ul": {
                    mb: 1.5,
                    pl: 4,
                    listStyle: "disc",
                    listStyleType: "disc",
                    listStylePosition: "outside",
                    display: "block",
                    WebkitPaddingStart: "24px",
                    paddingInlineStart: "24px",
                    "& ul": {
                      listStyleType: "circle",
                      mb: 0.5,
                    },
                    "& ul ul": {
                      listStyleType: "square",
                    },
                  },
                  "& ol": {
                    mb: 1.5,
                    pl: 4,
                    listStyleType: "decimal",
                    listStylePosition: "outside",
                    WebkitPaddingStart: "24px",
                    paddingInlineStart: "24px",
                  },
                  "& li": {
                    mb: 0.5,
                    lineHeight: 1.8,
                    color: "#333",
                    display: "list-item",
                    listStyle: "inherit",
                    listStyleType: "inherit",
                    paddingLeft: "4px",
                    "&::marker": {
                      color: "#000",
                      fontWeight: 500,
                    },
                  },
                  "& strong": {
                    fontWeight: 600,
                    color: "#000",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: subscriptionAgreement,
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ textTransform: "lowercase", color: "text.secondary" }}>
                subscription agreement not available.
              </Typography>
            );
          })()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionAgreementDialog;